// Helper murni untuk fitur "Bagikan produk ke WhatsApp" di halaman produk
// storefront (ProductDetail). Sengaja BEBAS dari Supabase/React supaya gampang
// diuji (lihat src/lib/__tests__/shareProduct.test.ts). UI memakai window.origin
// untuk membangun URL lalu helper-helper ini menyusun teks & link wa.me.

import {
  renderWaTemplate,
  resolveWaTemplate,
  type WaTemplateOverrides,
} from "@/lib/waTemplates";
import { formatPrice } from "@/lib/pricing";
import { WIB_TIME_ZONE } from "@/lib/wib";

export type ProductShareInput = {
  /** Nama produk (wajib). */
  productName: string;
  /** Nama toko untuk branding — kosong/null = dilewati. */
  storeName?: string | null;
  /** Harga siap-tampil (mis. "Rp 250.000") — kosong/null = dilewati. */
  priceLabel?: string | null;
  /** Label tipe produk, mis. "Pre-Order" / "Ready Stock" — kosong/null = dilewati. */
  typeLabel?: string | null;
  /**
   * Blok stok/sisa kuota siap-tempel dari `buildStockLines` (sudah termasuk
   * baris judul ber-cap waktu). Kosong/undefined = tak dicetak — itu keadaan
   * NORMAL untuk PO tanpa kuota, yang barangnya diproduksi mengikuti pesanan
   * sehingga angka stok tak bermakna.
   */
  stockLines?: string[] | null;
  /** URL halaman produk. */
  url: string;
};

/** Bangun URL publik halaman produk dari origin browser + slug. */
export function buildProductShareUrl(origin: string, slug: string): string {
  const base = (origin ?? "").replace(/\/+$/, "");
  return `${base}/product/${slug}`;
}

/**
 * Susun teks pesan WhatsApp untuk membagikan produk. Baris yang datanya kosong
 * (nama toko/harga/tipe/stok) otomatis dilewati agar pesan tetap rapi.
 */
export function buildProductShareText(
  input: ProductShareInput,
  overrides?: WaTemplateOverrides | null
): string {
  const price = input.priceLabel?.trim();
  const type = input.typeLabel?.trim();
  const stock = input.stockLines ?? [];

  return renderWaTemplate(resolveWaTemplate("bagikan_produk", overrides), {
    "[nama produk]": input.productName.trim(),
    "[nama toko]": input.storeName?.trim() ?? "",
    "[baris harga]": price ? `\u{1F4B0} ${price}` : "",
    "[baris jenis]": type ? `\u{1F6CD}\uFE0F ${type}` : "",
    "[blok stok]": stock.join("\n"),
    "[link produk]": input.url.trim()
  });
}

/**
 * Link "bagikan ke WhatsApp" tanpa nomor tujuan (wa.me/?text=…) → membuka
 * pemilih kontak WhatsApp dengan pesan sudah terisi.
 */
export function buildWhatsAppShareLink(text: string): string {
  return `https://wa.me/?text=${encodeURIComponent(text)}`;
}

// ─── Blok stok / sisa kuota di pesan WhatsApp ────────────────────────────────
//
// Pemilik meminta tombol Bagikan ikut membawa ANGKA per varian, bukan cuma nama
// & harga: pembeli grosir menanyakan "warna apa yang masih ada" sebelum membuka
// link, dan reseller selama ini mengetiknya manual.
//
// Ikut mencantumkan TANGGAL & JAM adalah bagian dari permintaan yang sama, tapi
// ia menaikkan taruhannya: sekali pesan menyebut jam, angkanya jadi KLAIM
// WAKTU. Karena itu `readAt` di sini WAJIB jam saat angkanya benar-benar
// DIBACA — bukan jam saat tombol ditekan. Halaman produk yang dibiarkan
// terbuka dua jam akan mengirim stok basi dua jam; menstempelnya "sekarang"
// berarti berbohong ke pembeli. Pemanggil (`ProductDetail`) membaca ulang stok
// saat klik dan menyerahkan jam pembacaan itu; kalau pembacaan gagal ia
// menyerahkan jam halaman memuat datanya, bukan jam sekarang.

export type StockShareKind = "stock" | "quota";

export type StockShareOption = { id: string; name: string };

export type StockShareVariant = {
  /** Nama kombinasi apa adanya — dipakai bila opsinya tak bisa diresolusi. */
  name: string;
  /** Opsi penyusun kombinasi ini (`product_variants.option_ids`). */
  optionIds?: string[];
  /** Angka yang dicetak: stok fisik (ready stock) ATAU sisa kuota (PO berkuota). */
  qty: number;
};

export type StockShareInput = {
  kind: StockShareKind;
  /** Grup varian urut tampil; grup PERTAMA jadi baris ➡️, sisanya digabung " | ". */
  groups: { options: StockShareOption[] }[];
  /** Varian urut tampil (sort_order). `qty` sudah diturunkan pemanggil. */
  variants: StockShareVariant[];
  /** Kapan angkanya DIBACA. Lihat catatan di atas — bukan jam kirim. */
  readAt: Date;
  /** Anggaran panjang blok stok (default `MAX_STOCK_CHARS`). */
  maxChars?: number;
};

/**
 * Jaring pengaman TERAKHIR, bukan gaya penyajian.
 *
 * Versi pertama memotong di 50 BARIS, dan itu keliru: produk 84 varian pun
 * kehilangan separuh daftarnya padahal muat dengan longgar. Pemilik menolaknya
 * (2026-09-14) — daftar stok memang materi jualannya, jadi SEMUA varian dicetak.
 *
 * Yang tersisa di sini cuma batas DAYA ANGKUT. Pesan masuk ke `wa.me/?text=`
 * dalam bentuk ter-encode (satu ➡️ saja jadi 18 karakter), dan WhatsApp sendiri
 * memotong pesan di 65.536 karakter — pemotongan yang terjadi DIAM-DIAM, di
 * tengah daftar, tanpa pembeli tahu ada yang hilang. Satu baris penutup yang
 * menunjuk link jauh lebih baik daripada itu.
 *
 * 20.000 karakter dipilih dari pengukuran PROD 2026-09-14, bukan dikarang:
 * produk TERBESAR yang benar-benar sampai ke fungsi ini butuh ~3.600 karakter
 * (`Elina series by em outfit`, 96 varian berkuota; ready stock terbesar 84
 * varian ≈ 3.000). Anggaran ini 5× lipatnya, masih ~30% dari batas WhatsApp,
 * dan ter-encode masih aman untuk URL di iOS maupun Android.
 *
 * Produk PO raksasa (sampai 1.920 kombinasi berkuota) TIDAK lewat sini sama
 * sekali — ia mode malas, dan pemanggilnya sudah menggerbangnya lebih dulu.
 */
export const MAX_STOCK_CHARS = 20_000;

const STOCK_HEADING: Record<StockShareKind, string> = {
  stock: "Stok",
  quota: "Sisa Kuota PO",
};

const STOCK_ALL_GONE: Record<StockShareKind, string> = {
  stock: "Semua varian habis",
  quota: "Semua kuota PO sudah habis",
};

/**
 * "8 September 2026, 21:45 WIB".
 *
 * Zona DIKUNCI ke WIB dan ditulis eksplisit — bukan zona perangkat pembagi.
 * Reseller di Makassar dan di Jakarta harus mengirim jam yang SAMA untuk stok
 * yang sama; memakai zona lokal proses membuat dua pesan yang isinya identik
 * mengaku dibaca pada jam berbeda (idiom `src/lib/wib.ts`).
 */
export function formatStockReadAt(readAt: Date): string {
  const tanggal = readAt.toLocaleDateString("id-ID", {
    day: "numeric",
    month: "long",
    year: "numeric",
    timeZone: WIB_TIME_ZONE,
  });
  // Jam dirangkai dari `formatToParts`, BUKAN `toLocaleTimeString("id-ID")`:
  // locale id-ID mencetak "21.45" (pemisah TITIK) sementara pesan ini memakai
  // "21:45". Merangkai dari bagian melepaskan hasilnya dari selera locale.
  const parts = new Intl.DateTimeFormat("en-GB", {
    timeZone: WIB_TIME_ZONE,
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
  }).formatToParts(readAt);
  const pick = (type: string) => parts.find((p) => p.type === type)?.value ?? "";
  return `${tanggal}, ${pick("hour")}:${pick("minute")} WIB`;
}

type StockLine = { text: string; variant: boolean; header: boolean };

/**
 * Susun blok stok siap-tempel (SUDAH termasuk baris judul ber-cap waktu).
 *
 * Bentuknya mengikuti cara reseller menulis manual di grup WhatsApp: grup varian
 * PERTAMA jadi baris ➡️, grup sisanya digabung " | " di baris 🔹.
 *
 *   Stok per 8 September 2026, 21:45 WIB:
 *   ➡️Kode A
 *   🔹L = 37 Pcs
 *   🔹XXL = 2 Pcs
 *
 * Produk satu grup tak punya dimensi kedua, jadi angkanya menempel di baris ➡️
 * (bukan baris 🔹 berlabel kosong).
 *
 * Varian habis DISEMBUNYIKAN (keputusan pemilik) — pesan ini materi jualan, dan
 * ketersediaan yang mengikat tetap ada di halaman produknya. Kalau SEMUA habis,
 * judul + cap waktunya tetap dicetak dengan satu baris penjelas: menghilangkan
 * blok ini seluruhnya akan terbaca sebagai "datanya gagal dimuat", bukan "habis".
 *
 * Nama opsi diambil dari `optionIds` × `groups`, BUKAN dari memecah
 * `variant.name` — di PROD ada baris yang namanya sudah menyimpang dari opsi
 * penyusunnya (lihat `deriveVariantLabel`). Kombinasi yang opsinya tak bisa
 * diresolusi jatuh ke nama varian apa adanya, bukan diam-diam dibuang.
 */
export function buildStockLines(input: StockShareInput): string[] {
  const heading = `${STOCK_HEADING[input.kind]} per ${formatStockReadAt(input.readAt)}:`;
  const available = input.variants.filter(
    (v) => Number.isFinite(v.qty) && v.qty > 0
  );
  if (available.length === 0) return [heading, STOCK_ALL_GONE[input.kind]];

  const groups = input.groups.filter((g) => g.options.length > 0);
  const max = Math.max(1, Math.floor(input.maxChars ?? MAX_STOCK_CHARS));

  // Produk tanpa sistem grup (produk lama / "langsung harga saja").
  if (groups.length === 0) {
    if (available.length === 1) return [`${heading} ${available[0].qty} Pcs`];
    const flat: StockLine[] = available.map((v) => ({
      text: `🔹${v.name} = ${v.qty} Pcs`,
      variant: true,
      header: false,
    }));
    return [heading, ...capStockLines(flat, max, available.length)];
  }

  const firstIds = new Set(groups[0].options.map((o) => o.id));
  const nameById = new Map<string, string>();
  // Posisi opsi DI DALAM grupnya — inilah urutan yang pembeli lihat di layar.
  const orderById = new Map<string, number>();
  for (const group of groups) {
    group.options.forEach((option, index) => {
      nameById.set(option.id, option.name);
      orderById.set(option.id, index);
    });
  }
  // Opsi tak dikenal jatuh ke BELAKANG, bukan ke depan: nilai negatif akan
  // membuat baris rusak justru memimpin daftar.
  const orderOf = (id: string | undefined) =>
    (id !== undefined ? orderById.get(id) : undefined) ?? Number.MAX_SAFE_INTEGER;

  type Bucket = {
    header: string | null;
    order: number;
    items: { label: string; order: number[]; fallback: string; qty: number }[];
  };
  const buckets: Bucket[] = [];
  const byKey = new Map<string, Bucket>();

  for (const variant of available) {
    const ids = variant.optionIds ?? [];
    const headId = ids.find((id) => firstIds.has(id));
    const header = headId ? nameById.get(headId) ?? null : null;
    // Kunci ember = ID opsi, bukan NAMANYA: dua opsi boleh bernama sama, dan
    // menggabungkannya akan melebur dua kolom varian yang berbeda jadi satu.
    const key = headId ?? "";
    let bucket = byKey.get(key);
    if (!bucket) {
      bucket = { header, order: orderOf(headId), items: [] };
      byKey.set(key, bucket);
      buckets.push(bucket);
    }
    const restIds = groups
      .slice(1)
      .map((group) => ids.find((oid) => group.options.some((o) => o.id === oid)));
    const label = restIds
      .map((id) => (id ? nameById.get(id) : undefined))
      .filter((name): name is string => Boolean(name))
      .join(" | ");
    bucket.items.push({
      label,
      order: restIds.map(orderOf),
      fallback: variant.name,
      qty: variant.qty,
    });
  }

  // URUTANNYA MENGIKUTI LAYAR, bukan `product_variants.sort_order`.
  //
  // Dua urutan itu memang berbeda: selektor varian di halaman produk merender
  // `variantGroups[].options` menurut `variant_options.sort_order`, sedangkan
  // baris varian punya `sort_order` sendiri yang lahir dari urutan pembuatannya.
  // Sampai 2026-09-14 blok ini memakai urutan kemunculan BARIS, sehingga pesan
  // WhatsApp menyebut warna & ukuran dalam susunan yang tidak cocok dengan yang
  // dilihat pembeli begitu ia membuka linknya — pada MAHESA SERAYA (84 varian)
  // keduanya melenceng jauh. Pemilik memintanya persis sama.
  //
  // `Array.prototype.sort` stabil, jadi kombinasi yang opsinya tak ter-resolusi
  // tetap memegang urutan aslinya, di belakang.
  buckets.sort((a, b) => a.order - b.order);
  for (const bucket of buckets) {
    bucket.items.sort((a, b) => {
      const depth = Math.max(a.order.length, b.order.length);
      for (let i = 0; i < depth; i++) {
        const diff =
          (a.order[i] ?? Number.MAX_SAFE_INTEGER) -
          (b.order[i] ?? Number.MAX_SAFE_INTEGER);
        if (diff !== 0) return diff;
      }
      return 0;
    });
  }

  // Satu grup saja → tak ada dimensi kedua untuk baris 🔹, angkanya menempel di
  // baris ➡️. Gerbangnya `groups.length`, BUKAN "labelnya kebetulan kosong":
  // produk dua grup yang opsi ukurannya sudah dihapus juga berlabel kosong, dan
  // meringkasnya jadi "➡️Kode A = 6 Pcs" menelan ukuran yang masih tersimpan di
  // nama varian.
  const isOneLiner = (bucket: Bucket) =>
    groups.length === 1 && bucket.header !== null && bucket.items.length === 1;

  const lines: StockLine[] = [];
  let prevOneLiner = false;
  for (const bucket of buckets) {
    const oneLiner = isOneLiner(bucket);
    // Baris kosong memisahkan BLOK (header + daftar ukurannya). Deretan
    // satu-baris tidak butuh pemisah — memberi jarak di sana memecah daftar
    // warna yang pendek jadi berhalaman-halaman di layar HP.
    if (lines.length > 0 && !(prevOneLiner && oneLiner)) {
      lines.push({ text: "", variant: false, header: false });
    }
    prevOneLiner = oneLiner;
    if (oneLiner) {
      lines.push({
        text: `➡️${bucket.header} = ${bucket.items[0].qty} Pcs`,
        variant: true,
        header: false,
      });
      continue;
    }
    if (bucket.header) {
      lines.push({
        text: `➡️${bucket.header}`,
        variant: false,
        header: true,
      });
    }
    for (const item of bucket.items) {
      lines.push({
        text: `🔹${item.label || item.fallback} = ${item.qty} Pcs`,
        variant: true,
        header: false,
      });
    }
  }

  return [heading, ...capStockLines(lines, max, available.length)];
}

/**
 * Potong di batas DAYA ANGKUT lalu rapikan ekornya: baris kosong menggantung
 * dan header ➡️ yang variannya keburu terpotong dibuang, supaya pesan tak
 * berakhir dengan grup yang isinya nihil.
 *
 * Dalam pemakaian nyata tak ada yang terpotong di sini — lihat `MAX_STOCK_CHARS`.
 */
function capStockLines(
  lines: StockLine[],
  maxChars: number,
  total: number
): string[] {
  const out: string[] = [];
  const headerTexts = new Set(lines.filter((l) => l.header).map((l) => l.text));
  let shown = 0;
  let chars = 0;
  for (const line of lines) {
    const next = chars + line.text.length + 1;
    // Varian PERTAMA selalu masuk: blok berisi judul + "dan N lainnya" saja
    // tak memberi tahu apa pun, betapa pun sempitnya anggarannya.
    if (line.variant && shown > 0 && next > maxChars) break;
    out.push(line.text);
    chars = next;
    if (line.variant) shown += 1;
  }

  while (
    out.length > 0 &&
    (out[out.length - 1].trim() === "" || headerTexts.has(out[out.length - 1]))
  ) {
    out.pop();
  }

  const sisa = total - shown;
  if (sisa > 0) {
    out.push("");
    out.push(`…dan ${sisa} varian lainnya, cek di link`);
  }
  return out;
}

/**
 * Label harga untuk pesan bagikan. Varian yang harganya BEDA-BEDA dirangkum
 * jadi rentang `Rp 145.000-155.000` — angka kedua tanpa "Rp", bentuk yang
 * dipakai pemilik saat menulis format ini. Harga seragam tetap satu angka.
 */
export function buildPriceRangeLabel(min: number, max: number): string {
  if (!(max > min)) return formatPrice(min);
  return `${formatPrice(min)}-${new Intl.NumberFormat("id-ID").format(max)}`;
}

export type SharePhotoResult = "shared" | "downloaded" | "cancelled";

/**
 * Bagikan foto produk via Web Share API (mobile) sehingga reseller bisa
 * memilih WhatsApp → Status langsung dari share sheet OS.
 *
 * Fallback ke download file bila browser tidak mendukung file sharing
 * (semua desktop browser, browser mobile lama).
 *
 * Throws jika fetch gambar gagal (network error / CORS).
 */
export async function sharePhotoToStatus(
  imageUrl: string,
  productName: string
): Promise<SharePhotoResult> {
  const response = await fetch(imageUrl);
  if (!response.ok)
    throw new Error(`Gagal mengunduh foto (${response.status})`);

  const blob = await response.blob();
  const ext = blob.type.includes("png") ? "png" : "jpg";
  // Bersihkan nama file dari karakter yang tidak valid di filesystem.
  const safeName =
    productName.trim().replace(/[/\\:*?"<>|]/g, "-") || "foto-produk";
  const file = new File([blob], `${safeName}.${ext}`, { type: blob.type });

  const canShareFiles =
    typeof navigator !== "undefined" &&
    typeof navigator.share === "function" &&
    typeof navigator.canShare === "function" &&
    navigator.canShare({ files: [file] });

  if (canShareFiles) {
    try {
      await navigator.share({ files: [file], title: productName });
      return "shared";
    } catch (err) {
      // AbortError = user menutup share sheet tanpa memilih → bukan error.
      if (err instanceof Error && err.name === "AbortError") return "cancelled";
      // Error lain (mis. share sheet crash) → fall through ke download.
    }
  }

  // Fallback: unduh gambar ke device.
  const objectUrl = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = objectUrl;
  a.download = `${safeName}.${ext}`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(objectUrl);
  return "downloaded";
}
